import React, { useEffect, useState, useRef } from "react";
import "./header.scss";
import { Link, useLocation } from "react-router-dom";
import { Container, Nav, Navbar, Modal, Form, Button } from "react-bootstrap";
import { HashLink } from "react-router-hash-link";

export default function Header() {
  const [activeSection, setActiveSection] = useState("home");
  const [isScrolled, setIsScrolled] = useState(false);
  const [expanded, setExpanded] = useState(false); // Controlled state for menu
  const navbarRef = useRef(null); // Ref for outside click detection
  const location = useLocation();

  // Handle Scroll for Navbar Styling
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Detect active section for HOME PAGE scrolling
  // Detect active section based on path and scroll
  useEffect(() => {
    // 1. Specific Pages
    if (location.pathname === "/safety-guidelines") {
      setActiveSection("safety-guidelines");
      return;
    }

    if (location.pathname === "/full-fleet") {
      setActiveSection("our_fleet");
      return;
    }

    if (location.pathname.startsWith("/fleet-detail")) {
      setActiveSection("");
      return;
    }

    // 2. Home Page (Scroll Observer)
    const sections = document.querySelectorAll("section[id]");
    if (sections.length > 0) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveSection(entry.target.id);
            }
          });
        },
        { threshold: 0.3 }
      );

      sections.forEach((section) => observer.observe(section));
      return () => observer.disconnect();
    }
  }, [location.pathname]);


  // CLOSE MODAL
  const [show, setShow] = useState(false);
  const handleClose = () => {
    setShow(false);
  };

  const handleBookNow = () => {
    setShow(true);
    setExpanded(false); // Close menu if open
  };

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [yachtName, setYachtName] = useState("Sail Boat");
  const [error, setError] = useState("");

  const handleBooking = (e) => {
    e.preventDefault();

    // Basic validation
    if (!name || !email || !phone) {
      setError("All fields are required!");
      return;
    }

    // Clear error if everything is fine
    setError("");

    // Dynamically generate the Razorpay link with the form values
    const razorpayUrl = `https://pages.razorpay.com/yachtmumbai?amount=2000&yacht_name=${encodeURIComponent(
      yachtName
    )}&name=${encodeURIComponent(name)}&email=${encodeURIComponent(
      email
    )}&phone=${encodeURIComponent(phone)}`;

    // Open Razorpay link in a new tab
    window.open(razorpayUrl, "_blank");
  };

  // Close menu on link click
  const closeMenu = () => setExpanded(false);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside); // Handle touch events too
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // Lock Body Scroll when menu is open
  useEffect(() => {
    if (expanded) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto"; // Cleanup
    };
  }, [expanded]);


  return (
    <React.Fragment>
      <Navbar
        ref={navbarRef}
        expand="lg"
        expanded={expanded}
        className={`my-navbar ${isScrolled ? "scrolled" : ""}`}
      >
        <Container>
          <Navbar.Brand as={Link} to="/" onClick={closeMenu} className="d-flex align-items-center">
            <img src="/boatmumbai.png" alt="Book Sailing Mumbai" className="brand-logo" />
            <span className="brand-text ms-2">Book Sailing Mumbai</span>
          </Navbar.Brand>
          <Navbar.Toggle
            aria-controls="basic-navbar-nav"
            onClick={() => setExpanded(expanded ? false : "expanded")}
          >
            {/* Custom Toggler Content could go here if we want to replace the default icon completely, 
                 but Bootstrap's default often works well. Let's start with default and see if we need a custom one for X */}
            {expanded ? <i className="btn-close-custom"></i> : <span className="navbar-toggler-icon"></span>}
          </Navbar.Toggle>

          <Navbar.Collapse id="basic-navbar-nav" className="justify-content-center">
            {/* CENTERED NAVIGATION */}
            <div className="nav-center-container">
              <Nav className="navbar-links center-links">
                {/* Home */}
                <Nav.Link
                  as={Link}
                  to="/"
                  eventKey="home"
                  className={activeSection === "home" ? "active" : ""}
                  onClick={closeMenu}
                >
                  Home
                </Nav.Link>

                {/* Experiences */}
                <Nav.Link
                  as={HashLink}
                  smooth
                  to="/#experiences"
                  eventKey="experiences"
                  className={activeSection === "experiences" ? "active" : ""}
                  onClick={closeMenu}
                >
                  Experiences
                </Nav.Link>

                {/* Fleet */}
                <Nav.Link
                  as={Link}
                  to="/full-fleet"
                  eventKey="our_fleet"
                  className={activeSection === "our_fleet" ? "active" : ""}
                  onClick={closeMenu}
                >
                  Our Fleet
                </Nav.Link>

                {/* SAFETY → NORMAL LINK */}
                <Nav.Link
                  as={Link}
                  to="/safety-guidelines"
                  eventKey="safety-guidelines"
                  className={activeSection === "safety-guidelines" ? "active" : ""}
                  onClick={closeMenu}
                >
                  Safety
                </Nav.Link>
              </Nav>
            </div>

            {/* RIGHT SIDE ACTIONS */}
            <Nav className="navbar-links right-actions">
              {/* Contact */}
              <Nav.Link
                as={HashLink}
                smooth
                to="/home#contact"
                eventKey="contact"
                className={`contact-us btn-color btn-effect ${activeSection === "contact" ? "active" : ""}`}
                onClick={closeMenu}
              >
                Contact Us
              </Nav.Link>


            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* MODAL */}
      <Modal
        className="event-celebrations-modal"
        show={show}
        onHide={handleClose}
        centered
      >
        <Modal.Header style={{ borderBottom: "1px solid #dee2e6" }} closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            Get Expert Advice
          </Modal.Title>
        </Modal.Header>
        <Form className="book-now-form" onSubmit={handleBooking}>
          <Modal.Body>
            {error && (
              <div style={{ color: "red", marginBottom: "10px" }}>{error}</div>
            )}
            <div className="mb-3">
              <Form.Select value={yachtName} onChange={(e) => setYachtName(e.target.value)}>
                <option>Sail Boats</option>
                <option>Sail Yachts</option>
                <option>Motor Yachts</option>
                <option>Speed Boats</option>
              </Form.Select>
            </div>
            <div className="mb-3">
              <Form.Control
                type="text"
                placeholder="Enter Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <Form.Control
                type="email"
                placeholder="Enter Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <Form.Control
                type="text"
                placeholder="Enter Phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </Modal.Body>
          <Modal.Footer className="d-block">
            <div className="book-event-bttn text-uppercase">
              <Button type="submit" variant="default" className="btn-color btn-effect w-100">
                Get Expert Advice
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>
    </React.Fragment>
  );
}